import React, { useCallback, useMemo, useState } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel, getFilteredRowModel } from '@tanstack/react-table';
import { FaCheck, FaTimes, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import { TiDocumentAdd } from 'react-icons/ti';
import { toast } from 'react-toastify';
import useOrganisationStore from '../../store/organisations-store';
import usePaginatedListPage from '../../shared/hooks/usePaginatedListPage';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import ListFooter from '../../shared/components/table/ListFooter';
import RowActions from '../../shared/components/table/RowActions';
import ResourceFormModal from '../../shared/components/modal/ResourceFormModal';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import UploadTemplateDoc from '../../shared/components/UploadTemplateDoc';
import { formatDate, trimData } from '../../shared/lib/formatters';

const columnHelper = createColumnHelper();

const sortIcon = (state) => {
  if (state === 'asc') return <FaSortUp className="inline size-4 text-gray-800" />;
  if (state === 'desc') return <FaSortDown className="inline size-4 text-gray-800" />;
  return <FaSort className="inline size-4 text-gray-700" />;
};

const organisationBaseFields = [
  { name: 'name', label: 'Name', type: 'text', required: true, colSpan: 2 },
  { name: 'tokens', label: 'Tokens', type: 'number', required: true },
  { name: 'max_word_limit', label: 'Max Word Limit', type: 'number', required: true },
];

const trialEndDateField = { name: 'trial_end_date', label: 'Trial End Date', type: 'datetime-local' };
const payingCustomerField = { name: 'paying_customer', label: 'Paying Customer', type: 'checkbox', colSpan: 2 };

const OrganisationsPage = () => {
  const { organisations, totalOrganisations, totalPages, fetchOrganisations, createOrganisation, updateOrganisation, deleteOrganisation } =
    useOrganisationStore();

  const [tokensSort, setTokensSort] = useState('');
  const [trialEndSort, setTrialEndSort] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateDocOpen, setTemplateDocOpen] = useState(false);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);

  const fetchOrganisationsFresh = useCallback(
    (page, search, tokensSortArg, trialEndSortArg) =>
      fetchOrganisations(page, search, tokensSortArg, trialEndSortArg, { force: true }),
    [fetchOrganisations]
  );

  const {
    page: currentPage,
    setPage: setCurrentPage,
    search: globalFilter,
    setSearch: setGlobalFilter,
    isLoading,
  } = usePaginatedListPage({
    fetchItems: fetchOrganisationsFresh,
    deps: [tokensSort, trialEndSort],
  });

  const handleTokenSort = () => {
    if (tokensSort === '') {
      setTokensSort('desc');
      setTrialEndSort('');
    } else if (tokensSort === 'desc') {
      setTokensSort('asc');
      setTrialEndSort('');
    } else {
      setTokensSort('');
    }
    setCurrentPage(1);
  };

  const handleTrialEndSort = () => {
    if (trialEndSort === '') {
      setTrialEndSort('desc');
      setTokensSort('');
    } else if (trialEndSort === 'desc') {
      setTrialEndSort('asc');
      setTokensSort('');
    } else {
      setTrialEndSort('');
    }
    setCurrentPage(1);
  };

  const openCreate = () => {
    setSelectedOrganisation(null);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const openEdit = (organisation) => {
    setSelectedOrganisation(organisation);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const openTemplateDoc = (organisation) => {
    setSelectedOrganisation(organisation);
    setTemplateDocOpen(true);
  };

  const openDelete = (organisation) => {
    setSelectedOrganisation(organisation);
    setIsDeleteModalOpen(true);
  };

  const closeModals = () => {
    setIsFormOpen(false);
    setIsDeleteModalOpen(false);
    setTemplateDocOpen(false);
    setSelectedOrganisation(null);
  };

  const handleFormSubmit = async (formData) => {
    const payload = trimData(formData);
    try {
      if (formMode === 'edit' && selectedOrganisation) {
        await updateOrganisation(selectedOrganisation.id, payload);
        toast.success('Organisation updated successfully');
      } else {
        await createOrganisation(payload);
        toast.success('Organisation created successfully');
      }
      closeModals();
    } catch (error) {
      console.error(`Failed to ${formMode === 'edit' ? 'update' : 'create'} organisation:`, error);
      toast.error(`Failed to ${formMode === 'edit' ? 'update' : 'create'} organisation. Please try again.`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteOrganisation(id);
      toast.success('Organisation deleted successfully');
      closeModals();
    } catch (error) {
      console.error('Failed to delete organisation:', error);
      toast.error('Failed to delete organisation. Please try again.');
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('tokens', {
        header: () => (
          <span
            onClick={handleTokenSort}
            className="inline-flex items-center gap-[6px] cursor-pointer select-none hover:text-gray-900"
          >
            Tokens {sortIcon(tokensSort)}
          </span>
        ),
      }),
      columnHelper.accessor('max_word_limit', { header: 'Max word limit' }),
      columnHelper.accessor('paying_customer', {
        header: 'Paying Customer',
        cell: (info) =>
          info.getValue() === true ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-600" />,
      }),
      columnHelper.accessor('docx_template_file_name', {
        header: 'Template File Name',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
      }),
      columnHelper.accessor('trial_end_date', {
        header: () => (
          <span
            onClick={handleTrialEndSort}
            className="inline-flex items-center gap-[6px] cursor-pointer select-none hover:text-gray-900"
          >
            Trial End Date {sortIcon(trialEndSort)}
          </span>
        ),
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('created_at', {
        header: 'Created At',
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions onEdit={() => openEdit(row.original)} onDelete={() => openDelete(row.original)}>
            <TiDocumentAdd
              size={20}
              title="Template Doc"
              onClick={() => openTemplateDoc(row.original)}
              className="cursor-pointer text-green-500"
            />
          </RowActions>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tokensSort, trialEndSort]
  );

  const data = useMemo(() => organisations, [organisations]);

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const noOrganisationsFound = table.getRowModel().rows.length === 0;

  const fields = useMemo(
    () =>
      formMode === 'edit'
        ? [...organisationBaseFields, trialEndDateField, payingCustomerField]
        : [...organisationBaseFields, payingCustomerField],
    [formMode]
  );

  const initialValues = useMemo(() => {
    if (formMode === 'edit' && selectedOrganisation) {
      return {
        name: selectedOrganisation.name || '',
        tokens: selectedOrganisation.tokens || 0,
        max_word_limit: selectedOrganisation.max_word_limit || 0,
        paying_customer: selectedOrganisation.paying_customer || false,
        trial_end_date: selectedOrganisation.trial_end_date
          ? new Date(selectedOrganisation.trial_end_date).toISOString().split('.')[0]
          : '',
      };
    }
    return { name: '', tokens: 0, max_word_limit: 0, paying_customer: false };
  }, [formMode, selectedOrganisation]);

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="Organisation List" onCreate={openCreate} />
      <div className="mb-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
      </div>
      <DataTable
        table={table}
        columnsCount={columns.length}
        isLoading={isLoading}
        emptyMessage="No organisations found"
        enableSortingIndicator={false}
      />
      <ListFooter
        total={totalOrganisations}
        totalPages={totalPages}
        page={currentPage}
        setPage={setCurrentPage}
        isLoading={isLoading}
        isEmpty={noOrganisationsFound}
        itemLabel="organisations"
      />
      <ResourceFormModal
        isOpen={isFormOpen}
        onClose={closeModals}
        mode={formMode}
        title={formMode === 'edit' ? 'Edit Organisation' : 'Create Organisation'}
        fields={fields}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        gridClassName="grid grid-cols-2 gap-4"
      />
      {selectedOrganisation && (
        <UploadTemplateDoc
          isOpen={templateDocOpen}
          onRequestClose={closeModals}
          entity={{ ...selectedOrganisation, type: 'organisations' }}
        />
      )}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeModals}
        data={{ ...selectedOrganisation, dataName: 'organisation' }}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default OrganisationsPage;
